import { api } from '@/config/api';

export type ShiftStatus = 'scheduled' | 'confirmed' | 'completed' | 'absent';
export const SHIFT_STATUSES: ShiftStatus[] = ['scheduled', 'confirmed', 'completed', 'absent'];

export const STAFF_ROLE_SUGGESTIONS = ['Manager', 'Chef', 'Cook', 'Waiter', 'Cashier', 'Barista', 'Driver', 'Cleaner'];

export interface StaffMember {
  staffUid: string;
  name: string;
  phone: string | null;
  role: string;
  userUid: string | null;
  hourlyRate: number | null;
  currency: string | null;
  color: string;
  isActive: boolean;
  notes: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface Shift {
  shiftUid: string;
  staffUid: string;
  date: string;
  start: string;
  end: string;
  breakMinutes: number;
  station: string | null;
  status: ShiftStatus;
  notes: string | null;
  overnight: boolean;
  hours: number;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ShiftsWeek {
  weekStart: string;
  weekEnd: string;
  days: string[];
  shifts: Shift[];
}

export interface WeekSummaryStaffRow {
  staffUid: string;
  name: string;
  role: string;
  color: string;
  hours: number;
  cost: number;
  shiftCount: number;
  currency: string;
}

export interface OnShiftNowRow {
  staffUid: string;
  name: string;
  role: string;
  shiftUid: string;
  start: string;
  end: string;
  station: string | null;
}

export interface WeekSummary {
  weekStart: string;
  weekEnd: string;
  currency: string;
  staff: WeekSummaryStaffRow[];
  totals: { hours: number; cost: number; staffCount: number };
  onShiftNow: OnShiftNowRow[];
}

export interface CreateStaffMemberInput {
  name: string;
  phone?: string;
  role: string;
  userUid?: string;
  hourlyRate?: number;
  currency?: string;
  color?: string;
  isActive?: boolean;
  notes?: string;
}

export type UpdateStaffMemberInput = Partial<CreateStaffMemberInput>;

export interface CreateShiftInput {
  staffUid: string;
  date: string;
  start: string;
  end: string;
  breakMinutes?: number;
  station?: string;
  status?: ShiftStatus;
  notes?: string;
}

export type UpdateShiftInput = Partial<CreateShiftInput>;

export const staffService = {
  listStaff: async (params?: { q?: string; active?: 'true' | 'false' }): Promise<{ items: StaffMember[] }> =>
    (await api.get('/slighter_crm/staff', { params })).data,

  getStaff: async (staffUid: string): Promise<StaffMember> =>
    (await api.get(`/slighter_crm/staff/${staffUid}`)).data,

  createStaff: async (p: CreateStaffMemberInput): Promise<StaffMember> =>
    (await api.post('/slighter_crm/staff', p)).data,

  updateStaff: async (staffUid: string, p: UpdateStaffMemberInput): Promise<StaffMember> =>
    (await api.patch(`/slighter_crm/staff/${staffUid}`, p)).data,

  removeStaff: async (staffUid: string): Promise<void> => { await api.delete(`/slighter_crm/staff/${staffUid}`); },

  listShifts: async (params?: { weekStart?: string; role?: string }): Promise<ShiftsWeek> =>
    (await api.get('/slighter_crm/staff/shifts', { params })).data,

  createShift: async (p: CreateShiftInput): Promise<Shift> =>
    (await api.post('/slighter_crm/staff/shifts', p)).data,

  updateShift: async (shiftUid: string, p: UpdateShiftInput): Promise<Shift> =>
    (await api.patch(`/slighter_crm/staff/shifts/${shiftUid}`, p)).data,

  setShiftStatus: async (shiftUid: string, status: ShiftStatus): Promise<Shift> =>
    (await api.patch(`/slighter_crm/staff/shifts/${shiftUid}/status`, { status })).data,

  removeShift: async (shiftUid: string): Promise<void> => { await api.delete(`/slighter_crm/staff/shifts/${shiftUid}`); },

  copyWeek: async (fromWeekStart: string, toWeekStart: string): Promise<{ copied: number; skipped: number; toWeekStart: string }> =>
    (await api.post('/slighter_crm/staff/shifts/copy-week', { fromWeekStart, toWeekStart })).data,

  weekSummary: async (weekStart?: string): Promise<WeekSummary> =>
    (await api.get('/slighter_crm/staff/summary', { params: weekStart ? { weekStart } : {} })).data,
};
